import React, { useState, useRef, useEffect } from "react";
import { ChatCircleDots, PaperPlaneTilt, ShareNetwork, Image as ImageIcon, ArrowCounterClockwise, Sparkle, Warning } from "@phosphor-icons/react";
import { api, formatCurrency } from "../../utils/api";
import { Button } from "../shared/Button";

// Chat privado del dueño (Admin), pensado para usar en el celular parado
// delante del cliente en la obra: preguntas rápidas sobre materiales/
// precios/calidades del catálogo real, respuestas cortas, y fotos reales
// listas para reenviar por WhatsApp con el botón "compartir" nativo del
// celular — funciona con o sin el número de WhatsApp Business (ya conectado
// desde el 11 sep 2026), no depende de él.
// Pendiente: solo hay un módulo cargado (Suelos) — sumar el resto cuando
// se arme el mismo tipo de catálogo real para Cocinas/Baños/etc.

const MODULOS_DISPONIBLES = [
  { id: "suelos", nombre: "Suelos", icono: "🪵" },
];

const puedeCompartirArchivos = () =>
  typeof navigator !== "undefined" && !!navigator.canShare && !!navigator.share;

const dataUriAFile = async (dataUri, nombreArchivo) => {
  const res = await fetch(dataUri);
  const blob = await res.blob();
  return new File([blob], nombreArchivo, { type: blob.type || "image/jpeg" });
};

export const AsistenteObra = ({ adminToken }) => {
  const [modulo, setModulo] = useState("suelos");
  const [mensajes, setMensajes] = useState([]); // {role, content, materiales_con_foto?}
  const [pregunta, setPregunta] = useState("");
  const [enviando, setEnviando] = useState(false);
  const [generandoImagen, setGenerandoImagen] = useState(false);
  const [error, setError] = useState(null);
  const finRef = useRef(null);

  useEffect(() => {
    finRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [mensajes, enviando]);

  const enviar = async () => {
    const texto = pregunta.trim();
    if (!texto || enviando) return;
    setError(null);
    setPregunta("");

    const historialParaBackend = mensajes.map((m) => ({ role: m.role, content: m.content }));
    setMensajes((prev) => [...prev, { role: "user", content: texto }]);
    setEnviando(true);

    try {
      const resp = await api.post(
        "/admin/asistente-obra/consulta",
        { pregunta: texto, historial: historialParaBackend, modulo },
        adminToken
      );
      setMensajes((prev) => [
        ...prev,
        { role: "assistant", content: resp.respuesta, materiales_con_foto: resp.materiales_con_foto || [] },
      ]);
    } catch (e) {
      console.error("Error asistente en obra:", e);
      setError("No se pudo consultar a la IA. Revisá la conexión e intentá de nuevo.");
      // Sacamos la pregunta que quedó "colgada" sin respuesta y la devolvemos
      // al input para poder reintentarla sin volver a escribirla.
      setMensajes((prev) => prev.slice(0, -1));
      setPregunta(texto);
    } finally {
      setEnviando(false);
    }
  };

  const pedirImagenReferencia = async () => {
    const descripcion = pregunta.trim();
    if (!descripcion || generandoImagen) return;
    setError(null);
    setPregunta("");
    setMensajes((prev) => [...prev, { role: "user", content: `🎨 Imagen de referencia: ${descripcion}` }]);
    setGenerandoImagen(true);

    try {
      const resp = await api.post(
        "/admin/asistente-obra/generar-imagen",
        { descripcion },
        adminToken
      );
      setMensajes((prev) => [
        ...prev,
        { role: "assistant", tipo: "imagen-referencia", imagen_url: resp.imagen_url, aviso: resp.aviso },
      ]);
    } catch (e) {
      console.error("Error generando imagen:", e);
      setError("No se pudo generar la imagen. Intentá de nuevo.");
      setMensajes((prev) => prev.slice(0, -1));
      setPregunta(descripcion);
    } finally {
      setGenerandoImagen(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      enviar();
    }
  };

  const compartirTexto = async (texto) => {
    if (puedeCompartirArchivos() || (typeof navigator !== "undefined" && navigator.share)) {
      try {
        await navigator.share({ text: texto });
        return;
      } catch (e) {
        // el usuario canceló el share nativo, no hacemos nada más
        if (e?.name === "AbortError") return;
      }
    }
    // Fallback desktop: abrir WhatsApp Web con el texto precargado
    window.open(`https://wa.me/?text=${encodeURIComponent(texto)}`, "_blank");
  };

  const compartirFoto = async (material) => {
    const caption = `${material.nombre}${material.marca ? " — " + material.marca : ""}${
      material.precio_std ? ` — ${formatCurrency(material.precio_std)}` : ""
    }`;
    try {
      if (puedeCompartirArchivos()) {
        const archivo = await dataUriAFile(material.foto_url, `${material.nombre}.jpg`);
        if (navigator.canShare({ files: [archivo] })) {
          await navigator.share({ files: [archivo], text: caption });
          return;
        }
      }
    } catch (e) {
      if (e?.name === "AbortError") return;
      console.error("Error compartiendo foto:", e);
    }
    // Fallback: abrir la foto en una pestaña nueva para que la guarde/comparta a mano
    const ventana = window.open();
    if (ventana) {
      ventana.document.write(`<img src="${material.foto_url}" style="max-width:100%" />`);
    }
  };

  // La advertencia va SIEMPRE pegada al texto que se comparte, aunque Andy
  // reenvíe la imagen sin leer la pantalla — así el cliente también la ve.
  const compartirImagenGenerada = async (mensaje) => {
    const caption = mensaje.aviso;
    try {
      if (puedeCompartirArchivos()) {
        const archivo = await dataUriAFile(mensaje.imagen_url, "referencia.png");
        if (navigator.canShare({ files: [archivo] })) {
          await navigator.share({ files: [archivo], text: caption });
          return;
        }
      }
    } catch (e) {
      if (e?.name === "AbortError") return;
      console.error("Error compartiendo imagen generada:", e);
    }
    const ventana = window.open();
    if (ventana) {
      ventana.document.write(
        `<p style="font-family:sans-serif;color:#b91c1c">${caption}</p><img src="${mensaje.imagen_url}" style="max-width:100%" />`
      );
    }
  };

  const reiniciar = () => {
    setMensajes([]);
    setError(null);
  };

  return (
    <div className="pb-24" style={{ display: "flex", flexDirection: "column", height: "calc(100vh - 140px)" }}>
      <div className="container pt-4" style={{ display: "flex", flexDirection: "column", flex: 1, minHeight: 0 }}>
        {/* Header */}
        <div className="flex items-center justify-between mb-3">
          <div>
            <h1 className="text-h2 font-display flex items-center gap-2">
              <ChatCircleDots size={28} weight="fill" style={{ color: "var(--brand-accent)" }} />
              Asistente en Obra
            </h1>
            <p className="text-small text-secondary">
              Preguntá rápido, con datos reales de tu catálogo — para vos, no para el cliente.
            </p>
          </div>
          {mensajes.length > 0 && (
            <Button variant="ghost" size="sm" onClick={reiniciar} icon={<ArrowCounterClockwise size={16} />}>
              Nueva charla
            </Button>
          )}
        </div>

        {/* Selector de módulo (por ahora solo Suelos) */}
        <div className="flex gap-2 mb-3">
          {MODULOS_DISPONIBLES.map((m) => (
            <button
              key={m.id}
              onClick={() => {
                if (m.id !== modulo) reiniciar();
                setModulo(m.id);
              }}
              className={`tab ${modulo === m.id ? "tab-active" : ""}`}
            >
              {m.icono} {m.nombre}
            </button>
          ))}
        </div>

        {/* Historial de mensajes */}
        <div
          className="card p-3 mb-3"
          style={{ flex: 1, minHeight: 0, overflowY: "auto", display: "flex", flexDirection: "column", gap: "0.75rem" }}
        >
          {mensajes.length === 0 && (
            <div className="text-small text-secondary text-center" style={{ padding: "2rem 1rem" }}>
              Ejemplo: "cliente quiere parquet flotante, qué le ofrezco y qué colores hay"
            </div>
          )}

          {mensajes.map((m, i) => {
            if (m.tipo === "imagen-referencia") {
              return (
                <div key={i} style={{ alignSelf: "flex-start", maxWidth: "88%" }}>
                  <div
                    className="p-2 rounded-lg text-tiny font-bold flex items-center gap-2 mb-1"
                    style={{ background: "var(--danger-bg)", color: "var(--danger)" }}
                  >
                    <Warning size={16} weight="fill" />
                    {m.aviso}
                  </div>
                  <img
                    src={m.imagen_url}
                    alt="Referencia visual generada por IA"
                    className="rounded-lg"
                    style={{ maxWidth: "220px", width: "100%" }}
                  />
                  <button
                    onClick={() => compartirImagenGenerada(m)}
                    className="text-tiny flex items-center gap-1 mt-1"
                    style={{ color: "var(--brand-accent)" }}
                  >
                    <ShareNetwork size={14} /> Compartir (con la advertencia)
                  </button>
                </div>
              );
            }
            return (
            <div key={i} style={{ alignSelf: m.role === "user" ? "flex-end" : "flex-start", maxWidth: "88%" }}>
              <div
                className="p-3 rounded-lg text-small"
                style={{
                  background: m.role === "user" ? "var(--brand-primary)" : "var(--surface-secondary)",
                  color: m.role === "user" ? "white" : "inherit",
                  whiteSpace: "pre-wrap",
                }}
              >
                {m.content}
              </div>

              {m.role === "assistant" && (
                <div className="flex items-center gap-2 mt-1">
                  <button
                    onClick={() => compartirTexto(m.content)}
                    className="text-tiny flex items-center gap-1"
                    style={{ color: "var(--brand-accent)" }}
                  >
                    <ShareNetwork size={14} /> Compartir texto
                  </button>
                </div>
              )}

              {m.materiales_con_foto && m.materiales_con_foto.length > 0 && (
                <div className="flex gap-2 mt-2" style={{ overflowX: "auto" }}>
                  {m.materiales_con_foto.map((mat) => (
                    <div
                      key={mat.id}
                      className="card p-2"
                      style={{ minWidth: "120px", maxWidth: "120px", flexShrink: 0 }}
                    >
                      <img
                        src={mat.foto_url}
                        alt={mat.nombre}
                        className="w-full rounded"
                        style={{ height: "80px", objectFit: "cover" }}
                      />
                      <div className="text-tiny font-bold mt-1" style={{ lineHeight: 1.2 }}>{mat.nombre}</div>
                      {mat.precio_std != null && (
                        <div className="text-tiny text-secondary">{formatCurrency(mat.precio_std)}</div>
                      )}
                      <button
                        onClick={() => compartirFoto(mat)}
                        className="text-tiny flex items-center gap-1 mt-1"
                        style={{ color: "var(--brand-accent)" }}
                      >
                        <ImageIcon size={12} /> Compartir
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
            );
          })}

          {enviando && (
            <div style={{ alignSelf: "flex-start" }} className="text-small text-secondary p-2">
              Pensando...
            </div>
          )}

          {generandoImagen && (
            <div style={{ alignSelf: "flex-start" }} className="text-small text-secondary p-2">
              Generando imagen de referencia...
            </div>
          )}

          {error && (
            <div className="text-small p-2 rounded-lg" style={{ background: "var(--danger-bg)", color: "var(--danger)" }}>
              {error}
            </div>
          )}

          <div ref={finRef} />
        </div>

        {/* Input */}
        <div className="flex gap-2 mb-2">
          <textarea
            value={pregunta}
            onChange={(e) => setPregunta(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Escribí tu pregunta..."
            rows={1}
            className="input flex-1"
            style={{ resize: "none" }}
            disabled={enviando || generandoImagen}
          />
          <Button
            variant="accent"
            onClick={enviar}
            disabled={enviando || generandoImagen || !pregunta.trim()}
            icon={<PaperPlaneTilt size={18} />}
          >
            Enviar
          </Button>
        </div>
        <button
          onClick={pedirImagenReferencia}
          disabled={enviando || generandoImagen || !pregunta.trim()}
          className="text-tiny flex items-center gap-1"
          style={{ color: "var(--brand-accent)", opacity: !pregunta.trim() ? 0.5 : 1 }}
        >
          <Sparkle size={14} /> Pedirle a la IA una imagen de referencia de lo escrito arriba (no es foto real)
        </button>
      </div>
    </div>
  );
};
